"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import CloseIcon from "@/components/_icons/closeIcon";
import { ImageModalCloseButton } from "@/components/_icons/imageModalCloseButton";
import DisableOverlay from "@/components/disableOverlay/disableOverlay";
import { ModalModifierSelection } from "./modalModifierSelection";
import { lightenColor } from "@/utils/lightenColor";
import { Service } from "@/dto/serviceItem";
import { Modifier } from "@/dto/modifer";

const MAX_QUANTITY = 100;
const MIN_QUANTITY = 1;

export const ServiceModalModifierSelection = ({
  isCheckoutEnabled,
  merchantName,
  onRequestClose,
  productImage,
  service,
  formatDuration,
  onShowEmployeeSelection,
  modifiers,
  selectedModifiers,
  setSelectedModifiers,
  menu,
  setFinalPrice,
  isAcceptingAppointments,
  merchantColor,
  quantity,
  setQuantity,
}: {
  isCheckoutEnabled: boolean;
  merchantName: string;
  onRequestClose: () => void;
  productImage: any;
  service: Service;
  formatDuration: (duration: string) => string;
  onShowEmployeeSelection: () => void;
  modifiers: any[];
  selectedModifiers: any;
  setSelectedModifiers: (modifiers: Modifier[]) => void;
  menu: any;
  setFinalPrice: (price: number) => void;
  isAcceptingAppointments: boolean;
  merchantColor: string;
  quantity: number | undefined;
  setQuantity: (quantity: number | undefined) => void;
}) => {
  const isPerUnitService = service?.priceType === "PER_UNIT";

  const filteredModifierGroups = menu.modifierGroups
    ? Object.values(menu.modifierGroups).filter((group: any) =>
        service.modifierGroupIds.includes(group.id)
      )
    : [];

  const handleModifierChange = (
    groupId: string,
    modifierId: string,
    isSingleSelection: boolean
  ) => {
    const modifier = modifiers.find((mod: any) => mod.id === modifierId);
    if (!modifier) {
      return;
    }

    const currentSelections = selectedModifiers[groupId] || [];
    const group: any = filteredModifierGroups.find(
      (g: any) => g.id === groupId
    );

    if (!group) return;

    if (isSingleSelection) {
      setSelectedModifiers({
        ...selectedModifiers,
        [groupId]: modifier,
      });
    } else {
      const isSelected = currentSelections.some(
        (mod: any) => mod.id === modifierId
      );

      if (!isSelected && currentSelections.length >= group.maxAllowed) {
        return;
      }

      const newSelections = isSelected
        ? currentSelections.filter((mod: any) => mod.id !== modifierId)
        : [...currentSelections, modifier];

      setSelectedModifiers({
        ...selectedModifiers,
        [groupId]: newSelections,
      });
    }
  };

  const validateModifierSelection = () => {
    return filteredModifierGroups.every((group: any) => {
      const selected = selectedModifiers[group.id] || [];
      const isGroupValid =
        group.minRequired === 0 ||
        (Array.isArray(selected)
          ? selected.length >= group.minRequired
          : !!selected);
      return isGroupValid;
    });
  };

  const [inputValue, setInputValue] = useState<string>(
    quantity?.toString() ?? ""
  );
  const [isMinusDisabled, setIsMinusDisabled] = useState(
    quantity ? quantity <= MIN_QUANTITY : true
  );
  const [isPlusDisabled, setIsPlusDisabled] = useState(
    quantity ? quantity >= MAX_QUANTITY : false
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (value === "") {
      setQuantity(undefined);
      return;
    }

    const num = parseInt(value, 10);
    if (!isNaN(num) && num > 0 && num <= MAX_QUANTITY) {
      setQuantity(num);
    } else {
      setQuantity(undefined);
    }
  };

  const handleInputBlur = () => {
    if (quantity === undefined) {
      setQuantity(MIN_QUANTITY);
      setInputValue(String(MIN_QUANTITY));
    }
  };

  const handleDecrement = () => {
    const newQuantity = (quantity ?? MIN_QUANTITY + 1) - 1;
    if (newQuantity >= MIN_QUANTITY) {
      setQuantity(newQuantity);
    }
  };

  const handleIncrement = () => {
    const newQuantity = (quantity ?? MIN_QUANTITY - 1) + 1;
    if (newQuantity <= MAX_QUANTITY) {
      setQuantity(newQuantity);
    }
  };

  useEffect(() => {
    setInputValue(quantity?.toString() ?? "");
  }, [quantity]);

  useEffect(() => {
    if (quantity === undefined) {
      setIsMinusDisabled(false); // Allow decrementing to MIN_QUANTITY
      setIsPlusDisabled(false);
    } else {
      setIsMinusDisabled(quantity <= MIN_QUANTITY);
      setIsPlusDisabled(quantity >= MAX_QUANTITY);
    }
  }, [quantity]);

  const isValidSelection = validateModifierSelection();
  let disableBookButton =
    service?.available === false ||
    !isValidSelection ||
    !isAcceptingAppointments;

  return (
    <div className="relative bg-white sm:rounded-lg shadow-lg w-full sm:max-w-[900px] flex flex-col sm:flex-row-reverse sm:w-auto mx-auto h-[100%] sm:max-h-[576px] overflow-y-auto sm:overflow-x-visible">
      <button
        onClick={onRequestClose}
        className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 z-10"
      >
        {productImage ? <ImageModalCloseButton /> : <CloseIcon />}
      </button>
      {productImage && (
        <div className="flex flex-1 flex-shrink-0 sm:max-h-full sm:w-[50%]">
          <Image
            width={450}
            height={576}
            alt={service.name || "service name"}
            src={productImage.source}
            className="object-cover object-center w-full h-full"
          />
        </div>
      )}
      <div
        className={`flex flex-1 flex-col sm:min-h-[260px] w-full ${
          productImage ? "sm:w-[50%]" : "sm:w-[530px]"
        } relative md:h-full sm:h-auto overflow-y-visible sm:overflow-y-auto`}
      >
        <DisableOverlay
          merchantName={merchantName}
          isCheckoutEnabled={isCheckoutEnabled}
        >
          <div className="flex flex-col px-6 pt-6">
            <h2 className="pt-4 text-4xl text-[32px] font-medium text-[#373737] whitespace-nowrap overflow-hidden text-ellipsis">
              {service.name}
            </h2>
            <p className="text-[15px] font-medium pt-4">
              from ${(service.price / 100).toFixed(2)}
              {isPerUnitService && <> per {service.unitName}</>}{" "}
              {service.serviceItem && (
                <span className="">
                  &nbsp;&bull;&nbsp;{" "}
                  {formatDuration(service.serviceItem.duration) || "0 min"}
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-col px-6 max-h-[400px] sm:max-h-[420px] overflow-y-visible sm:overflow-y-auto mb-8 pb-[150px] sm:pb-0">
            <div className="py-4">
              <div className="text-gray-500 break-words whitespace-pre-wrap">
                {service.description && <p>{service.description}</p>}
              </div>
            </div>
            <ModalModifierSelection
              {...{
                filteredModifierGroups,
                modifiers,
                selectedModifiers,
                handleModifierChange,
                merchantColor,
              }}
            />
          </div>
          {isPerUnitService && (
            <PerUnitBottomButtons
              {...{
                disableBookButton,
                merchantColor,
                setFinalPrice,
                service,
                selectedModifiers,
                onShowEmployeeSelection,
                isAcceptingAppointments,
                quantity,
                inputValue,
                handleInputChange,
                handleInputBlur,
                handleDecrement,
                handleIncrement,
                perUnitName: service?.unitName,
                isMinusDisabled,
                isPlusDisabled,
              }}
            />
          )}
          {!isPerUnitService && (
            <FixedPriceTypeBottomButtons
              {...{
                disableBookButton,
                merchantColor,
                setFinalPrice,
                service,
                selectedModifiers,
                onShowEmployeeSelection,
                isAcceptingAppointments,
              }}
            />
          )}
        </DisableOverlay>
      </div>
    </div>
  );
};

const PerUnitBottomButtons = ({
  disableBookButton,
  merchantColor,
  setFinalPrice,
  service,
  selectedModifiers,
  onShowEmployeeSelection,
  isAcceptingAppointments,
  quantity,
  inputValue,
  handleInputChange,
  handleInputBlur,
  handleDecrement,
  handleIncrement,
  perUnitName,
  isMinusDisabled,
  isPlusDisabled,
}: any) => {
  const mathFontStyle: React.CSSProperties = {
    fontFamily: "'Menlo', 'Lucida Console', 'Monaco', 'Consolas', monospace",
  };
  return (
    <div className="fixed sm:absolute bottom-0 left-0 w-full sm:h-[64px] flex flex-col sm:flex-row">
      <div
        style={{
          backgroundColor: lightenColor(merchantColor, 90),
        }}
        className="bg-[#2E2E2E] disabled:bg-[#E5E5E5] text-white text-xl font-medium sm:w-2/5 flex flex-row rounded-bl-md h-[64px]"
      >
        <button
          onClick={handleDecrement}
          className="pl-3 py-1 text-[30px] flex items-center flex-1 justify-center text-[#727272] pb-2"
          style={mathFontStyle}
          disabled={isMinusDisabled}
        >
          -
        </button>
        <div className="flex flex-row items-center">
          <input
            id="input-quantity"
            value={inputValue}
            placeholder={String(MIN_QUANTITY)}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            className="text-center text-black"
            style={{
              appearance: "textfield",
              MozAppearance: "textfield",
              WebkitAppearance: "none",
              backgroundColor: lightenColor(merchantColor, 90),
              width: `${Math.max(inputValue.length, 3)}ch`,
            }}
            inputMode="numeric"
          />
          <span className="font-medium text-black">{perUnitName}</span>
        </div>
        <button
          onClick={handleIncrement}
          className="px-3 py-1 text-[30px] flex items-center flex-1 justify-center text-[#727272] pb-2"
          style={mathFontStyle}
          disabled={isPlusDisabled}
        >
          +
        </button>
      </div>
      <button
        style={{
          backgroundColor: disableBookButton ? "#C7C7C7" : merchantColor,
        }}
        className="flex sm:w-3/5 w-full h-[64px] bg-[#2E2E2E] disabled:bg-[#E5E5E5] text-white text-xl font-medium"
        onClick={() => {
          setFinalPrice(getPrice(service, selectedModifiers, quantity));
          onShowEmployeeSelection();
        }}
        disabled={disableBookButton}
      >
        {service?.available === false || !isAcceptingAppointments ? (
          <div className="text-[#373737] text-[15px]">
            <p>Sorry, this service is currently unavailable. </p>
          </div>
        ) : (
          <div className="flex w-full h-full flex-row px-6 items-center justify-between">
            <p>Book&nbsp;</p>
            <p>
              $
              {(getPrice(service, selectedModifiers, quantity) / 100).toFixed(
                2
              )}
            </p>
          </div>
        )}
      </button>
    </div>
  );
};

const FixedPriceTypeBottomButtons = ({
  disableBookButton,
  merchantColor,
  setFinalPrice,
  service,
  selectedModifiers,
  onShowEmployeeSelection,
  isAcceptingAppointments,
}: any) => {
  return (
    <button
      style={{
        backgroundColor: disableBookButton ? "#C7C7C7" : merchantColor,
      }}
      className="fixed sm:absolute bottom-0 left-0 w-full h-[64px] flex flex-col sm:flex-row bg-[#2E2E2E] disabled:bg-[#E5E5E5] text-white text-xl font-medium rounded-bl-none sm:rounded-bl-md"
      onClick={() => {
        setFinalPrice(getPrice(service, selectedModifiers, 1));
        onShowEmployeeSelection();
      }}
      disabled={disableBookButton}
    >
      {service?.available === false || !isAcceptingAppointments ? (
        <div className="text-[#373737] text-[15px]">
          <p>Sorry, this service is currently unavailable. </p>
        </div>
      ) : (
        <div className="flex w-full h-full flex-row px-6 items-center justify-between">
          <p>Book&nbsp;</p>
          <p>${(getPrice(service, selectedModifiers, 1) / 100).toFixed(2)}</p>
        </div>
      )}
    </button>
  );
};

const getPrice = (item: any, selectedModifiers: any, quantity?: any) => {
  if (!quantity) {
    quantity = MIN_QUANTITY;
  }
  return (
    item.price * quantity +
    Object.values(selectedModifiers)
      .flat()
      .reduce((total: number, modifier: any) => {
        return total + (modifier ? modifier.price : 0);
      }, 0)
  );
};
